import { Collapse, Empty, Spin } from 'antd';
import { Helmet } from 'react-helmet-async';

import useAPIQuery from '../../api/useAPIQuery';
import useInfiniteAPIQuery from '../../api/useInfiniteAPIQuery';
import CandidateAnsweredQuestionnairePanel from '../../components/CandidateAnsweredQuestionnairePanel/CandidateAnsweredQuestionnairePanel';
import ErrorResult from '../../components/ErrorResult';
import usePrefetchPages from '../../hooks/usePrefetchPages';
import useTypedParams, { Optional } from '../../hooks/useTypedParams';
import { withRouteChange } from '../../services/APM';
import {
  AnswerTextCollection,
  CandidateId,
  JobPostingId,
  QuestionCollection,
  RecruitmentProcessDetail,
} from '../../types';
import styles from '../ApplicantDetails.module.css';

const CandidateQuestionnaire = () => {
  const { id, applicant_id } = useTypedParams({
    id: Optional(JobPostingId),
    applicant_id: CandidateId,
  });

  const candidate = useAPIQuery(`/candidates/${applicant_id}/`);

  const recruitmentProcessesPages = useInfiniteAPIQuery([
    '/recruitment-processes/',
    {
      params: {
        candidate: applicant_id,
        has_answered_questions: true,
      },
    },
  ]);
  const recruitmentProcesses = usePrefetchPages(recruitmentProcessesPages, (data) => data.results);

  const questionsPages = useInfiniteAPIQuery(
    [
      '/questions/',
      {
        params: {
          job_posting_id__in: recruitmentProcesses
            .map((recruitmentProcess) => recruitmentProcess.job_posting.id)
            .join(','),
        },
      },
    ],
    {
      enabled: recruitmentProcesses.length > 0,
    }
  );

  const questions = usePrefetchPages(questionsPages, (data) => data.results);

  const questionsDict: Record<RecruitmentProcessDetail['id'], QuestionCollection[]> = {};
  for (const recruitmentProcess of recruitmentProcesses) {
    const jobPostingId = recruitmentProcess.job_posting.id;
    questionsDict[recruitmentProcess.id] = questions.filter(
      (question) => question.job_posting === jobPostingId
    );
  }

  const textQuestionsAnswersPages = useInfiniteAPIQuery(
    [
      '/answer-texts/',
      {
        params: {
          recruitment_process__candidate__id: applicant_id,
        },
      },
    ],
    {
      enabled: recruitmentProcesses.length > 0,
    }
  );

  const textQuestionsAnswers = usePrefetchPages(textQuestionsAnswersPages, (data) => data.results);

  const textQuestionsAnswersDict: Record<QuestionCollection['id'], AnswerTextCollection> = {};
  for (const textAnswer of textQuestionsAnswers) {
    textQuestionsAnswersDict[textAnswer.question] = textAnswer;
  }

  const answerAlternativesPages = useInfiniteAPIQuery(
    [
      '/answer-alternatives/',
      {
        params: {
          recruitment_process__candidate__id: applicant_id,
        },
      },
    ],
    {
      enabled: recruitmentProcesses.length > 0,
    }
  );

  const answerAlternatives = usePrefetchPages(answerAlternativesPages, (data) =>
    data.results.map((alternative_answer) => alternative_answer.question_alternative)
  );

  return (
    <ErrorResult error={recruitmentProcessesPages.error}>
      <Helmet>
        <title>Questionários | {candidate.data?.name || '...'} | Recrutamento e Seleção</title>
      </Helmet>
      <Spin
        spinning={
          candidate.isLoading ||
          recruitmentProcessesPages.isLoading ||
          questionsPages.isLoading ||
          textQuestionsAnswersPages.isLoading ||
          answerAlternativesPages.isLoading
        }
      >
        {(recruitmentProcesses.length ?? 0) > 0 ? (
          <Collapse
            ghost
            className={styles.collapse}
            expandIconPosition="end"
            defaultActiveKey={[`answeredQuestionnaire-${id}`]}
          >
            {recruitmentProcesses.map((recruitmentProcess) => {
              return (
                <CandidateAnsweredQuestionnairePanel
                  jobPostingName={recruitmentProcess.job_posting.name}
                  questions={questionsDict[recruitmentProcess.id]}
                  textQuestionsAnswers={textQuestionsAnswersDict}
                  answerAlternatives={answerAlternatives}
                  key={`answeredQuestionnaire-${recruitmentProcess.job_posting.id}`}
                />
              );
            })}
          </Collapse>
        ) : (
          <Empty />
        )}
      </Spin>
    </ErrorResult>
  );
};

export default withRouteChange()(CandidateQuestionnaire);
